import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ModalDirective } from 'ngx-bootstrap';
import { CaseService } from '../services/case.service';
import { BlockUiService } from '../services/blockui.service';
import { StorageService } from '../services/storage.service';
import { ExternalCaseService } from '../services/external-case.service';
import { NomenclatureService } from '../services/nomenclature.service';
import { ExternalCase } from '../beans/external-case';
import { Lawyer } from '../beans/Lawyer';
import { NationalHolidayService } from '../services/national-holiday.service';
import { NationalHoliday } from '../beans/national-holiday';
import { VacationService } from '../services/vacation.service';
import { VacationDialogComponent } from '../vacation-dialog/vacation-dialog.component';
import { ReportService } from '../services/report.service';
import * as FileSaver from 'file-saver';

interface CalendarDay {
	date: Date;
	dayOfMonth: number;
	isToday: boolean;
	isCurrentMonth: boolean;
	cases: any[];
	holidayName?: string;
	vacations?: any[];
}

@Component({
	selector: 'app-my-calendar',
	templateUrl: './my-calendar.component.html',
	styleUrls: ['./my-calendar.component.scss']
})
export class MyCalendarComponent implements OnInit, OnDestroy {

	private pollTimer: any;

	@ViewChild('editCase') editCase;
	@ViewChild('extCaseModal') extCaseModal: ModalDirective;
	@ViewChild('vacationDialog') vacationDialog: VacationDialogComponent;

	weekDays = ['Пон', 'Вто', 'Сря', 'Чет', 'Пет', 'Съб', 'Нед'];
	calendarWeeks: CalendarDay[][] = [];
	currentMonth: Date;
	monthLabel = '';
	allCases: any[] = [];
	isExtended = false;
	extendedWindowDays = 93; // трябва да съвпада с fromDays в ListController.getNasrocheniDela
	currentUserColor = '#FFFFFF';

	tooltipVisible = false;
	tooltipTop = 0;
	tooltipLeft = 0;
	tooltipCase: any = {};
	tooltipWarning = '';

	// National holidays
	nationalHolidays: NationalHoliday[] = [];

	// Vacations for calendar display
	allVacations: any[] = [];

	// External case modal
	extCase: ExternalCase = new ExternalCase();
	extCaseDate: Date = null;
	extCaseTime: string = null;
	extCaseLawyerId: number = null;
	lawyers: Lawyer[] = [];

	constructor(
		private caseService: CaseService,
		private blockUi: BlockUiService,
		private vcRef: ViewContainerRef,
		private datePipe: DatePipe,
		private storageService: StorageService,
		private externalCaseService: ExternalCaseService,
		private nomenclatureService: NomenclatureService,
		private nationalHolidayService: NationalHolidayService,
		private vacationService: VacationService,
		private reportService: ReportService
	) { }

	ngOnInit() {
		this.currentUserColor = this.storageService.getLoginResponse().color || '#FFFFFF';
		this.currentMonth = new Date();
		this.currentMonth.setDate(1);
		this.nationalHolidayService.getAll().subscribe(h => {
			this.nationalHolidays = h;
			this.loadCases();
		});
		this.loadLawyers();
		this.pollTimer = setInterval(() => this.loadCasesSilent(), 30000);
	}

	private loadLawyers() {
		this.nomenclatureService.getLawyers().subscribe(users => {
			this.nomenclatureService.getNdoOtgovornici().subscribe(ndo => {
				const merged = [...users];
				for (const n of ndo) {
					if (!merged.find(u => u.id === n.id)) {
						merged.push(n);
					}
				}
				merged.sort((a, b) => (a.names || '').localeCompare(b.names || ''));
				this.lawyers = merged;
			});
		});
	}

	ngOnDestroy() {
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
		}
	}

	prevMonth() {
		this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
		this.buildCalendar();
	}

	nextMonth() {
		this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
		this.buildCalendar();
	}

	goToday() {
		this.currentMonth = new Date();
		this.currentMonth.setDate(1);
		this.buildCalendar();
	}

	exportExcel() {
		const year = this.currentMonth.getFullYear();
		const month = this.currentMonth.getMonth() + 1;
		this.blockUi.start(this.vcRef);
		this.reportService.executeCalendarXLSReport(year, month, this.isExtended).subscribe(
			resp => {
				this.blockUi.stop();
				let filename = 'calendar_' + year + '_' + ('0' + month).slice(-2) + '.xlsx';
				const disposition = resp.headers.get('Content-Disposition');
				if (disposition) {
					const match = disposition.match(/filename="?([^"]+)"?/);
					if (match) { filename = match[1]; }
				}
				const blob = new Blob([resp.body], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				});
				FileSaver.saveAs(blob, filename);
			},
			() => {
				this.blockUi.stop();
				alert('Грешка при генериране на Excel файл.');
			}
		);
	}

	loadCases() {
		this.blockUi.start(this.vcRef);

		if (this.isExtended) {
			// Общ календар: само насрочени дела (граждански + наказателни) + външни дела
			// Без отпуски, без нотификации
			this.caseService.getNasrocheniDelaByUser(true).subscribe(
				nasrocheni => {
					const hearings = this.mapNasrocheniDela(nasrocheni);
					this.externalCaseService.getCalendarEntries(true).subscribe(
						extCases => {
							this.allCases = [...hearings, ...this.mapExternalCases(extCases)];
							this.allVacations = [];
							this.buildCalendar();
							this.blockUi.stop();
						},
						() => {
							this.allCases = hearings;
							this.allVacations = [];
							this.buildCalendar();
							this.blockUi.stop();
						}
					);
				},
				() => { this.blockUi.stop(); }
			);
		} else {
			// Личен календар: нотификации + външни дела
			this.caseService.getCalendarNotifications(false).subscribe(
				lst => {
					const notifications = this.mapNotifications(lst);
					this.externalCaseService.getCalendarEntries(false).subscribe(
						extCases => {
							this.allCases = [...notifications, ...this.mapExternalCases(extCases)];
							this.loadVacationsForMonth(() => {
								this.buildCalendar();
								this.blockUi.stop();
							});
						},
						() => {
							this.allCases = notifications;
							this.loadVacationsForMonth(() => {
								this.buildCalendar();
								this.blockUi.stop();
							});
						}
					);
				},
				() => { this.blockUi.stop(); }
			);
		}
	}

	loadCasesSilent() {
		if (this.isExtended) {
			this.caseService.getNasrocheniDelaByUser(true).subscribe(
				nasrocheni => {
					const hearings = this.mapNasrocheniDela(nasrocheni);
					this.externalCaseService.getCalendarEntries(true).subscribe(
						extCases => {
							this.allCases = [...hearings, ...this.mapExternalCases(extCases)];
							this.allVacations = [];
							this.buildCalendar();
						}
					);
				}
			);
		} else {
			this.caseService.getCalendarNotifications(false).subscribe(
				lst => {
					const notifications = this.mapNotifications(lst);
					this.externalCaseService.getCalendarEntries(false).subscribe(
						extCases => {
							this.allCases = [...notifications, ...this.mapExternalCases(extCases)];
							this.loadVacationsForMonth(() => this.buildCalendar());
						}
					);
				}
			);
		}
	}

	private loadVacationsForMonth(callback: () => void) {
		const year = this.currentMonth.getFullYear();
		const month = this.currentMonth.getMonth();
		const start = new Date(year, month - 1, 1);
		const end = new Date(year, month + 2, 0);
		const startStr = this.toIsoDate(start);
		const endStr = this.toIsoDate(end);
		this.vacationService.getByDateRange(startStr, endStr).subscribe(
			vacations => {
				this.allVacations = vacations;
				callback();
			},
			() => {
				this.allVacations = [];
				callback();
			}
		);
	}

	private toIsoDate(d: Date): string {
		return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
	}

	private mapNasrocheniDela(lst: any[]): any[] {
		return lst.map(d => ({
			dataNasrochen: d.dataNasrochen,
			timeNasrochen: d.timeNasrochen || null,
			case_number: d.case_number || '',
			claimantList: d.claimantList || '',
			defendantList: d.defendantList || '',
			court: d.court || '',
			lawyer: d.lawyer || '',
			lawyerColor: d.lawyerColor || null,
			lawyerId: d.lawyerId || null,
			note: d.note || '',
			id: d.id,
			notificationId: null,
			isNotification: false,
			isExternalCase: false,
			externalCaseId: null
		}));
	}

	private mapExternalCases(lst: any[]): any[] {
		return lst.map(ec => ({
			dataNasrochen: ec.scheduledDate,
			timeNasrochen: ec.scheduledTime || null,
			case_number: '',
			claimantList: '',
			defendantList: '',
			court: '',
			lawyer: ec.lawyerName || '',
			lawyerColor: ec.lawyerColor || null,
			lawyerId: ec.lawyerId || null,
			note: ec.note,
			id: null,
			notificationId: null,
			isNotification: true,
			isExternalCase: true,
			externalCaseId: ec.id,
			notificationDescription: 'Външно дело: ' + (ec.number || '')
		}));
	}

	private mapNotifications(lst: any[]): any[] {
		return lst.map(n => {
			const bk = n.businessKey || '';
			const isExtCase = bk.startsWith('EXTERNAL_CASE@ID=');
			const extCaseId = isExtCase ? Number(bk.replace('EXTERNAL_CASE@ID=', '')) : null;
			return {
				dataNasrochen: n.deadline,
				timeNasrochen: null,
				case_number: '',
				claimantList: '',
				defendantList: '',
				court: '',
				lawyer: n.lawyer || '',
				lawyerColor: n.lawyerColor || null,
				responsibleUserId: n.responsibleUserId || null,
				note: n.description,
				id: n.lawsuiteId,
				notificationId: n.id,
				isNotification: true,
				isExternalCase: isExtCase,
				externalCaseId: extCaseId,
				notificationDescription: n.description
			};
		});
	}

	buildCalendar() {
		const year = this.currentMonth.getFullYear();
		const month = this.currentMonth.getMonth();

		this.monthLabel = this.getMonthName(month) + ' ' + year;

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		// First day of month, adjusted to Monday start
		const firstDay = new Date(year, month, 1);
		let startDay = firstDay.getDay() - 1; // Monday = 0
		if (startDay < 0) startDay = 6; // Sunday

		// Last day of month
		const lastDay = new Date(year, month + 1, 0).getDate();

		// Build day map from cases
		const casesByDate: { [key: string]: any[] } = {};
		for (const c of this.allCases) {
			const d = this.parseDate(c.dataNasrochen);
			if (!d) continue;
			const key = this.dateKey(d);
			if (!casesByDate[key]) casesByDate[key] = [];
			casesByDate[key].push(c);
		}

		// Sort cases within each day: with time first (sorted), without time last
		for (const key of Object.keys(casesByDate)) {
			casesByDate[key].sort((a, b) => {
				const aTime = a.timeNasrochen || '';
				const bTime = b.timeNasrochen || '';
				if (aTime && !bTime) return -1;
				if (!aTime && bTime) return 1;
				return aTime.localeCompare(bTime);
			});
		}

		// Build weeks
		this.calendarWeeks = [];
		let currentWeek: CalendarDay[] = [];

		// Fill leading days from previous month
		const prevMonthLastDay = new Date(year, month, 0).getDate();
		for (let i = startDay - 1; i >= 0; i--) {
			const date = new Date(year, month - 1, prevMonthLastDay - i);
			currentWeek.push(this.createDay(date, false, today, casesByDate));
		}

		// Fill current month
		for (let day = 1; day <= lastDay; day++) {
			const date = new Date(year, month, day);
			currentWeek.push(this.createDay(date, true, today, casesByDate));
			if (currentWeek.length === 7) {
				this.calendarWeeks.push(currentWeek);
				currentWeek = [];
			}
		}

		// Fill trailing days from next month
		if (currentWeek.length > 0) {
			let nextDay = 1;
			while (currentWeek.length < 7) {
				const date = new Date(year, month + 1, nextDay++);
				currentWeek.push(this.createDay(date, false, today, casesByDate));
			}
			this.calendarWeeks.push(currentWeek);
		}
	}

	private createDay(date: Date, isCurrentMonth: boolean, today: Date, casesByDate: { [key: string]: any[] }): CalendarDay {
		const key = this.dateKey(date);
		const dayOfMonth = date.getDate();
		const month = date.getMonth() + 1;

		// Match national holiday
		const holiday = this.nationalHolidays.find(h => h.day === dayOfMonth && h.month === month);

		// Match vacations overlapping this day
		const dayVacations = this.allVacations.filter(v => {
			const start = this.parseDate(v.startDate);
			const end = this.parseDate(v.endDate);
			if (!start || !end) return false;
			return date >= start && date <= end;
		});

		return {
			date: date,
			dayOfMonth: dayOfMonth,
			isToday: date.getTime() === today.getTime(),
			isCurrentMonth: isCurrentMonth,
			cases: casesByDate[key] || [],
			holidayName: holiday ? holiday.name : null,
			vacations: dayVacations.length > 0 ? dayVacations : null
		};
	}

	private parseDate(value: any): Date {
		if (!value) return null;
		if (value instanceof Date) return value;
		// array [yyyy, mm, dd] (Java LocalDate serialization)
		if (Array.isArray(value) && value.length === 3) {
			return new Date(value[0], value[1] - 1, value[2]);
		}
		// timestamp number
		if (typeof value === 'number') {
			const d = new Date(value);
			d.setHours(0, 0, 0, 0);
			return d;
		}
		// "dd.MM.yyyy"
		if (typeof value === 'string' && value.includes('.')) {
			const [dd, mm, yyyy] = value.split('.');
			return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
		}
		// "yyyy-MM-dd"
		if (typeof value === 'string' && value.includes('-')) {
			const [yyyy, mm, dd] = value.split('-');
			return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
		}
		return null;
	}

	private dateKey(d: Date): string {
		return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
	}

	openCase(caseItem: any) {
		if (caseItem.isExternalCase && caseItem.externalCaseId) {
			this.openEditExtCase(caseItem.externalCaseId);
			return;
		}
		if (caseItem.isNotification && !caseItem.id) {
			return;
		}
		this.editCase.editCaseFromDelo(caseItem);
	}

	private openEditExtCase(extCaseId: number) {
		this.externalCaseService.getById(extCaseId).subscribe(ec => {
			this.extCase = ec;
			this.extCaseDate = ec.scheduledDate ? this.caseService.fromBackendDate(ec.scheduledDate) : null;
			this.extCaseTime = ec.scheduledTime || null;
			this.extCaseLawyerId = ec.lawyer ? ec.lawyer.id : null;
			this.extCaseEditMode = true;
			this.extCaseModal.show();
		});
	}

	canEditExtCase(): boolean {
		if (!this.extCaseEditMode) return true;
		const login = this.storageService.getLoginResponse();
		if (login.authority === 'ROLE_ADMIN') return true;
		return this.extCase && this.extCase.lawyer && this.extCase.lawyer.id === login.id;
	}

	canDeleteCalendarItem(caseItem: any): boolean {
		const login = this.storageService.getLoginResponse();
		if (login.authority === 'ROLE_ADMIN') return true;
		if (caseItem.isExternalCase && caseItem.lawyerId) {
			return caseItem.lawyerId === login.id;
		}
		if (caseItem.responsibleUserId) {
			return caseItem.responsibleUserId === login.id;
		}
		return false;
	}

	removeNotification(event: MouseEvent, caseItem: any) {
		event.stopPropagation();
		if (!caseItem.notificationId) {
			return;
		}
		if (!confirm('Сигурни ли сте, че искате да премахнете тази нотификация от календара?')) {
			return;
		}
		this.caseService.deactivateNotification(caseItem.notificationId).subscribe(
			() => this.loadCasesSilent(),
			() => alert('Грешка при премахване на нотификацията.')
		);
	}

	hasTime(caseItem: any): boolean {
		return caseItem.timeNasrochen != null && caseItem.timeNasrochen !== '';
	}

	getCaseLabel(caseItem: any): string {
		if (caseItem.isNotification) {
			const desc = caseItem.notificationDescription || '';
			return desc.length > 25 ? desc.substring(0, 25) + '...' : desc;
		}
		const time = caseItem.timeNasrochen || '';
		const number = caseItem.case_number || '';
		if (time) {
			return time + ' ' + number;
		}
		return number || 'Дело';
	}

	showTooltip(event: MouseEvent, caseItem: any, day?: CalendarDay) {
		this.tooltipCase = caseItem;
		if (caseItem.isNotification) {
			this.tooltipWarning = '';
		} else if (!this.hasTime(caseItem)) {
			this.tooltipWarning = 'Делото няма час';
		} else {
			this.tooltipWarning = '';
		}
		const rect = (event.target as HTMLElement).getBoundingClientRect();
		this.tooltipTop = rect.top - 8;
		this.tooltipLeft = rect.left;
		this.tooltipVisible = true;
	}

	hideTooltip() {
		this.tooltipVisible = false;
	}

	darkenColor(hex: string): string {
		if (!hex || hex.length < 7) return hex;
		const r = Math.max(0, parseInt(hex.substr(1, 2), 16) - 40);
		const g = Math.max(0, parseInt(hex.substr(3, 2), 16) - 40);
		const b = Math.max(0, parseInt(hex.substr(5, 2), 16) - 40);
		return '#' + [r, g, b].map(v => ('0' + v.toString(16)).slice(-2)).join('');
	}

	private getMonthName(month: number): string {
		const names = [
			'Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни',
			'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
		];
		return names[month];
	}

	// --- External case from calendar ---

	openAddExtCase(day: CalendarDay) {
		this.extCase = new ExternalCase();
		this.extCaseDate = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate(), 12, 0, 0);
		this.extCaseTime = null;
		this.extCaseLawyerId = this.storageService.getLoginResponse().id;
		this.extCaseEditMode = false;
		this.extCaseModal.show();
	}

	addMenuDay: any = null;

	toggleAddMenu(day: any, event: MouseEvent) {
		this.addMenuDay = this.addMenuDay === day ? null : day;
	}

	closeAddMenu() {
		this.addMenuDay = null;
	}

	openVacationDialog(day?: CalendarDay) {
		if (this.vacationDialog) {
			this.vacationDialog.show(day ? day.date : undefined);
		}
	}

	canDeleteVacation(vacation: any): boolean {
		const login = this.storageService.getLoginResponse();
		if (login.authority === 'ROLE_ADMIN') return true;
		return vacation.user && vacation.user.id === login.id;
	}

	deleteVacation(event: MouseEvent, vacation: any) {
		event.stopPropagation();
		if (!vacation.id) return;
		if (!confirm('Сигурни ли сте, че искате да изтриете тази отпуска?')) return;
		this.vacationService.delete(vacation.id).subscribe(
			() => this.loadCasesSilent(),
			() => alert('Грешка при изтриване на отпуската.')
		);
	}

	extCaseEditMode = false;

	saveExtCase() {
		const dto: any = {
			id: this.extCase.id || null,
			version: this.extCase.version != null ? this.extCase.version : null,
			number: this.extCase.number,
			scheduledDate: this.caseService.toBackendIsoDate(this.extCaseDate),
			scheduledTime: this.extCaseTime || null,
			lawyer: this.extCaseLawyerId ? { id: Number(this.extCaseLawyerId) } : null,
			note: this.extCase.note
		};

		const obs = this.extCaseEditMode
			? this.externalCaseService.update(dto)
			: this.externalCaseService.add(dto);

		obs.subscribe(
			() => {
				this.extCaseModal.hide();
				this.loadCases();
			},
			(err) => {
				alert('Грешка при запис: ' + ((err.error && err.error.message) || err.message || 'Неизвестна грешка'));
			}
		);
	}
}
